import React, { useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { toast } from "react-toastify";
import { match as Match } from "react-router-dom";
import Block from "../../../../components/Tailwind/Block";
import Header from "../../../../components/Tailwind/Block/Header";
import { EnumPermissionEntity } from "../../../../lib/ground-aws-graphql-core/api/graphql/types";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../components/Form";
import overrideClasses from "../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import { isString } from "../../../../utils/types";

interface Props {
  match?: Match<{ id: string }>;
  intl: IntlShape;
  edition: boolean;
}

const PriceUnitVariationForm = (props: Props): JSX.Element => {
  const { intl, match, edition } = props;
  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [togglePanel, setTogglePanel] = useState(false);

  const getPriceUnitVariation = GroundGraphqlContextStore.useStoreActions(
    actions => actions.priceUnitVariations.getPriceUnitVariation
  );

  const createPriceUnitVariation = GroundGraphqlContextStore.useStoreActions(
    actions => actions.priceUnitVariations.createPriceUnitVariation
  );

  const updatePriceUnitVariation = GroundGraphqlContextStore.useStoreActions(
    actions => actions.priceUnitVariations.updatePriceUnitVariation
  );

  const setPriceUnitVariation = GroundGraphqlContextStore.useStoreActions(
    actions => actions.priceUnitVariations.setPriceUnitVariation
  );

  const priceUnitVariation = GroundGraphqlContextStore.useStoreState(
    state => state.priceUnitVariations.priceUnitVariation
  );

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "code",
      label: "page.price.unit.variation.code",
      placeholder: "page.price.unit.variation.code.placeholder",
      initialValue: priceUnitVariation?.code,
      required: true,
    },
    {
      name: "label",
      label: "page.price.unit.variation.label",
      placeholder: "page.price.unit.variation.label.placeholder",
      initialValue: priceUnitVariation?.label,
      required: true,
      translatable: true,
    },
  ];

  const fetchData = () => {
    setLoading(true);
    if (match?.params.id) {
      getPriceUnitVariation({ id: match?.params.id }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Clean the event from the state when we unmount this component
    return () => {
      setPriceUnitVariation(null);
    };
  }, []);

  useEffect(() => {
    if (priceUnitVariation) setEnabled(priceUnitVariation.enabled);
  }, [priceUnitVariation]);

  const handleShowSavePanel = () => {
    setTogglePanel(true);
  };

  const handleSubmit = (values: any) => {
    setLoading(true);
    const { label, ...others } = values;
    if (edition && priceUnitVariation) {
      updatePriceUnitVariation({
        ...others,
        label: isString(label) ? JSON.parse(label) : label,
        id: priceUnitVariation.id,
        enabled: enabled,
      })
        .then(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.PRICE_UNIT_VARIATIONS}`);
          toast(
            intl.formatMessage({
              id: "page.price.unit.variation.update.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.PRICE_UNIT_VARIATIONS}`);
          toast(
            intl.formatMessage({
              id: "page.price.unit.variation.update.error",
            }),
            {
              type: "error",
            }
          );
        });
    } else {
      createPriceUnitVariation({
        ...others,
        label: isString(label) ? JSON.parse(label) : label,
        enabled,
      })
        .then(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.PRICE_UNIT_VARIATIONS}`);
          toast(
            intl.formatMessage({
              id: "page.price.unit.variation.create.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.PRICE_UNIT_VARIATIONS}`);
          toast(
            intl.formatMessage({
              id: "page.price.unit.variation.create.error",
            }),
            { type: "error" }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
        <Block>
          <Header
            item={priceUnitVariation}
            checked={enabled}
            title={
              edition
                ? "page.list.price.unit.variations.update.price.unit.variation"
                : "page.list.price.unit.variations.create.price.unit.variation"
            }
            entity={EnumPermissionEntity.PRICE_UNIT_VARIATION}
            onChange={e => {
              setEnabled(e);

              // show save panel
              handleShowSavePanel();
            }}
            className="border-b border-gray-200"
          />

          <DefaultForm
            fields={formFields}
            onSubmit={handleSubmit}
            showPanel={togglePanel}
          />
        </Block>
      </div>
    </>
  );
};

export default PriceUnitVariationForm;
